import React, { useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { serializeNodes } from '@plone/volto-slate/editor/render';

const getHref = (link) => {
  const item = Array.isArray(link) ? link[0] : link;
  if (!item) return '#';
  const url = item['@id'] || '#';
  const isFile = item['@type'] === 'File';
  return isFile ? `${url}/@@download/file` : url;
};

const extractTOCEntries = (blocks, layout, useH2 = true, useH3 = true, useBlockTitles = false) => {
  const entries = [];
  const allowedTypes = [
    ...(useH2 ? ['heading-two', 'h2'] : []),
    ...(useH3 ? ['heading-three', 'h3'] : []),
  ];

  layout.forEach((blockId) => {
    const block = blocks[blockId];
    
    // Extract from slate/text blocks (headings)
    if ((block?.['@type'] === 'text' || block?.['@type'] === 'slate') && Array.isArray(block.value)) {
      block.value.forEach((node) => {
        if (allowedTypes.includes(node.type)) {
          const text = node.children?.map((child) => child.text).join('').trim();
          const anchor = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          if (text) {
            entries.push({ id: anchor, title: text });
          }
        }
      });
    }
    
    // Extract from block titles (if enabled)
    if (useBlockTitles && block) {
      const titleField = block.headline || block.title || block.heading || block.headerText;
      if (titleField && typeof titleField === 'string' && titleField.trim()) {
        const text = titleField.trim();
        const anchor = text
          .toLowerCase()
          .replace(/<[^>]*>/g, '')
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
        entries.push({ id: anchor, title: text, isBlockTitle: true, blockId });
      }
    }
  });

  return entries;
};

// Get a class name from the background color value
const getBackgroundColorClass = (backgroundColor) => {
  if (!backgroundColor || backgroundColor === '') return 'bg-gradient';
  if (backgroundColor === 'none') return 'bg-none';
  
  // Extract the rgba values to determine the color and opacity level
  if (backgroundColor.includes('0, 0, 0')) {
    // Black overlays
    if (backgroundColor.includes('0.3')) return 'bg-black-light';
    if (backgroundColor.includes('0.5')) return 'bg-black-medium';
    if (backgroundColor.includes('0.7')) return 'bg-black-dark';
    return 'bg-black-overlay';
  }
  
  if (backgroundColor.includes('255, 255, 255')) {
    // White overlays
    if (backgroundColor.includes('0.3')) return 'bg-white-light';
    if (backgroundColor.includes('0.5')) return 'bg-white-medium';
    if (backgroundColor.includes('0.7')) return 'bg-white-dark';
    return 'bg-white-overlay';
  }
  
  // Color tints
  if (backgroundColor.includes('--blue-tint')) return 'bg-blue-tint';
  if (backgroundColor.includes('--teal-tint')) return 'bg-teal-tint';
  if (backgroundColor.includes('--turquoise-tint')) return 'bg-turquoise-tint';
  if (backgroundColor.includes('--gold-tint')) return 'bg-gold-tint';
  if (backgroundColor.includes('--coral-tint')) return 'bg-coral-tint';
  
  return 'bg-custom-overlay';
};

// Render Slate rich text content
const renderSlateContent = (contentBlocks) => {
  if (!contentBlocks || !Array.isArray(contentBlocks) || contentBlocks.length === 0) return null;
  
  try {
    return serializeNodes(contentBlocks);
  } catch (error) {
    console.error('Error rendering slate content:', error);
    return null;
  }
};

// Image Stack Component with Dynamic Height Calculation
const ImageStack = ({ images }) => {
  const containerRef = React.useRef(null);
  const [containerHeight, setContainerHeight] = React.useState(0);
  
  // Filter out empty images
  const validImages = images.filter(img => img);
  
  // Calculate container height based on actual image positions and dimensions
  const calculateHeight = React.useCallback(() => {
    if (!containerRef.current) return;
    
    const items = containerRef.current.querySelectorAll('.hero-image-stack-item');
    if (items.length === 0) return;
    
    let maxBottom = 0;
    
    items.forEach((item) => {
      const rect = item.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const relativeBottom = rect.bottom - containerRect.top;
      maxBottom = Math.max(maxBottom, relativeBottom);
    });
    
    // Add some padding at the bottom for safety
    const newHeight = Math.ceil(maxBottom + 20);
    setContainerHeight(newHeight);
  }, []);
  
  // Calculate height after images load and on window resize
  React.useEffect(() => {
    if (validImages.length === 0) return;
    
    // Wait for images to load before calculating
    const container = containerRef.current;
    if (!container) return;
    
    const imageElements = container.querySelectorAll('img');
    let loadedCount = 0;
    
    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === imageElements.length) {
        // Small delay to ensure layout is complete
        setTimeout(calculateHeight, 100);
      }
    };
    
    imageElements.forEach((img) => {
      if (img.complete) {
        checkAllLoaded();
      } else {
        img.addEventListener('load', checkAllLoaded);
      }
    });
    
    // Recalculate on window resize
    const handleResize = () => {
      calculateHeight();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Initial calculation after mount
    setTimeout(calculateHeight, 200);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      imageElements.forEach((img) => {
        img.removeEventListener('load', checkAllLoaded);
      });
    };
  }, [validImages.length, calculateHeight]);
  
  if (validImages.length === 0) return null;

  // Positioning that accounts for container padding
  const getImageStyle = (index, totalImages) => {
    // For single image, use 80% width centered
    if (totalImages === 1) {
      return {
        transform: 'rotate(-2deg)',
        zIndex: 1,
        width: '80%',
        left: '10%',
        top: '10px',
      };
    }
    
    // For two images - reduced rotations to stay within bounds
    if (totalImages === 2) {
      const imageStyles = [
        {
          transform: 'rotate(-2deg)',
          zIndex: 2,
          width: '52%',
          left: '2%',
          top: '40px',
        },
        {
          transform: 'rotate(3deg)',
          zIndex: 1,
          width: '52%',
          right: '2%',
          top: '10px',
        },
      ];
      return imageStyles[index] || imageStyles[0];
    }
    
    // For three images
    const imageStyles = [
      {
        transform: 'rotate(-2deg)',
        zIndex: 2,
        width: '52%',
        left: '2%',
        top: '40px',
      },
      {
        transform: 'rotate(3deg)',
        zIndex: 1,
        width: '52%',
        right: '2%',
        top: '10px',
      },
      {
        transform: 'rotate(-1deg)',
        zIndex: 3,
        width: '52%',
        left: '24%',
        top: '260px',
      }
    ];
    
    return imageStyles[index] || imageStyles[0];
  };

  return (
    <div 
      ref={containerRef}
      className={`hero-image-stack images-${validImages.length}`}
      style={containerHeight > 0 ? { height: `${containerHeight}px` } : {}}
    >
      {validImages.map((imageUrl, index) => (
        <div 
          key={index} 
          className="hero-image-stack-item"
          style={getImageStyle(index, validImages.length)}
        >
          <img src={imageUrl} alt="" />
        </div>
      ))}
    </div>
  );
};

const HeroItem = ({ hero, properties, isFirst, usePageOptions, blocks, layout, displayMode, sharedBackgroundUrl, sharedVideoUrl, heroIndex, carouselMode, layoutMode, decorativeImages }) => {
  const pageTitle = properties?.title || '';
  const pageDescription = properties?.description || '';
  const baseUrl = properties?.['@id'] || '';

  // Determine title and subtitle
  let displayTitle = hero.title;
  let displaySubtitle = hero.subtitle;
  let displayPreheader = hero.preheader;
  let publicationDate = null;
  let formattedDate = null;

  if (isFirst && usePageOptions) {
    if (usePageOptions.usePageTitle) {
      displayTitle = pageTitle;
    }
    if (usePageOptions.usePageDescription) {
      displaySubtitle = pageDescription;
    }
    if (usePageOptions.showPublicationDate) {
      publicationDate = properties?.effective;
      formattedDate = publicationDate ? new Date(publicationDate).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      }) : null;
      displayPreheader = formattedDate;
    }
  }

  // Determine background
  let imageUrl = '';
  let videoUrl = null;

  if (displayMode === 'shared' && sharedBackgroundUrl) {
    imageUrl = sharedBackgroundUrl;
    videoUrl = sharedVideoUrl;
  } else {
    // Individual background
    if (isFirst && usePageOptions?.usePreviewImage) {
      const previewImageUrl = `${baseUrl}/@@images/preview_image`;
      const defaultImageUrl = `${baseUrl}/@@images/image`;
      const hasPreviewImage = properties?.preview_image || properties?.image;
      imageUrl = hasPreviewImage && properties?.preview_image ? previewImageUrl : defaultImageUrl;
    } else {
      const image = hero.backgroundImage?.[0];
      imageUrl = image ? `${image['@id']}/@@images/image` : '';
    }

    // Video
    const rawId = hero.backgroundVideo?.[0]?.['@id'] || '';
    const filename = rawId.split('/').filter(Boolean).pop();
    const isAlreadyDownloadUrl = rawId.includes('/@@download/');
    videoUrl = rawId && filename
      ? (isAlreadyDownloadUrl ? rawId : `${rawId}/@@download/file/${filename}`)
      : null;
  }

  // TOC - only for first hero
  let tocEntries = [];
  if (isFirst && hero.useTOC && blocks && layout) {
    tocEntries = extractTOCEntries(blocks, layout, hero.useH2 ?? true, hero.useH3 ?? true, hero.useBlockTitles ?? false);
  }

  const alignment = hero.alignment || 'left';
  const backgroundColor = hero.backgroundColor || '';
  const foregroundColor = hero.foregroundColor || 'light';
  const topPaddingDesktop = hero.topPaddingDesktop || 'default';
  const topPaddingMobile = hero.topPaddingMobile || 'default';
  
  // Determine if we should show gradient overlay
  const showGradient = !backgroundColor || backgroundColor === '';
  const showColorOverlay = backgroundColor && backgroundColor !== '' && backgroundColor !== 'none';
  
  // Get background color class for styling hooks
  const bgColorClass = getBackgroundColorClass(backgroundColor);

  const TitleTag = isFirst ? 'h1' : 'h2';
  
  // Wrapper class for carousel slide
  const wrapperClass = carouselMode ? 'embla__slide' : `hero-item hero-item-${heroIndex + 1}`;
  
  // Add image stack layout class
  const isImageStackLayout = (layoutMode === 'imageStackRight' || layoutMode === 'imageStackLeft') && isFirst && !carouselMode;
  const isImageStackLeft = layoutMode === 'imageStackLeft' && isFirst && !carouselMode;

  // Check if content blocks exist
  const hasContentBlocks = hero.contentBlocks && Array.isArray(hero.contentBlocks) && hero.contentBlocks.length > 0;

  return (
    <div 
      id={hero.heroId || undefined}
      className={`${wrapperClass} ${bgColorClass} foreground-${foregroundColor} padding-desktop-${topPaddingDesktop} padding-mobile-${topPaddingMobile} ${isImageStackLayout ? (isImageStackLeft ? 'image-stack-layout image-stack-left' : 'image-stack-layout image-stack-right') : ''}`}
    >
      {displayMode !== 'shared' && (
        <>
          {videoUrl ? (
            <video
              className="hero-video-bg"
              autoPlay
              muted
              loop
              playsInline
              poster={imageUrl}
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : (
            <div
              className="hero-image-bg"
              style={{ backgroundImage: `url(${imageUrl})` }}
            />
          )}
        </>
      )}
      {showColorOverlay && (
        <div className="hero-color-overlay" style={{ backgroundColor }} />
      )}
      {showGradient && <div className="overlay" />}
      <div className={`custom-hero-inner align-${alignment} ${hero.horizontalLayout ? 'horizontal-layout' : ''}`}>
        {hero.horizontalLayout ? (
          <>
            <div className="custom-hero-content">
              {displayPreheader && <div className="hero-preheader">{displayPreheader}</div>}
              {displayTitle && <TitleTag>{displayTitle}</TitleTag>}
            </div>
            {(displaySubtitle || hasContentBlocks || (isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0)) && (
              <div className="subtitle-buttons-wrapper">
                {displaySubtitle && <div className="subtitle">{displaySubtitle}</div>}
                {hasContentBlocks && (
                  <div className="hero-content-blocks">
                    {renderSlateContent(hero.contentBlocks)}
                  </div>
                )}
                {(isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0) && (
                  <div className={`custom-hero-buttons${hero.smallButtons ? ' small-buttons' : ''}`}>
                    {isFirst && hero.useTOC
                      ? tocEntries.map((entry, i) => (
                          <a key={i} href={`#${entry.id}`} className="hero-button white-outline">
                            {entry.title}
                          </a>
                        ))
                      : hero.buttons.map((btn, i) => {
                          const href = getHref(btn.link);
                          const buttonStyle = btn.buttonStyle || 'white-outline';
                          const isExternal = (() => {
                            try {
                              const url = new URL(href, window.location.origin);
                              return url.hostname !== window.location.hostname;
                            } catch {
                              return false;
                            }
                          })();
                          return (
                            <a
                              key={i}
                              href={href}
                              className={`hero-button ${buttonStyle}`}
                              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            >
                              {btn.label || 'Click here'}
                            </a>
                          );
                        })}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {isImageStackLayout ? (
              <div className="hero-content-with-images">
                <div className="custom-hero-content">
                  {displayPreheader && <div className="hero-preheader">{displayPreheader}</div>}
                  {displayTitle && <TitleTag>{displayTitle}</TitleTag>}
                  {displaySubtitle && <div className="subtitle">{displaySubtitle}</div>}
                  {hasContentBlocks && (
                    <div className="hero-content-blocks">
                      {renderSlateContent(hero.contentBlocks)}
                    </div>
                  )}
                  {(isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0) && (
                    <div className={`custom-hero-buttons${hero.smallButtons ? ' small-buttons' : ''}`}>
                      {isFirst && hero.useTOC
                        ? tocEntries.map((entry, i) => (
                            <a key={i} href={`#${entry.id}`} className="hero-button white-outline">
                              {entry.title}
                            </a>
                          ))
                        : hero.buttons.map((btn, i) => {
                            const href = getHref(btn.link);
                            const buttonStyle = btn.buttonStyle || 'white-outline';
                            const isExternal = (() => {
                              try {
                                const url = new URL(href, window.location.origin);
                                return url.hostname !== window.location.hostname;
                              } catch {
                                return false;
                              }
                            })();
                            return (
                              <a
                                key={i}
                                href={href}
                                className={`hero-button ${buttonStyle}`}
                                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                              >
                                {btn.label || 'Click here'}
                              </a>
                            );
                          })}
                    </div>
                  )}
                </div>
                <ImageStack images={decorativeImages} />
              </div>
            ) : (
              <>
                <div className="custom-hero-content">
                  {displayPreheader && <div className="hero-preheader">{displayPreheader}</div>}
                  {displayTitle && <TitleTag>{displayTitle}</TitleTag>}
                  {displaySubtitle && <div className="subtitle">{displaySubtitle}</div>}
                  {hasContentBlocks && (
                    <div className="hero-content-blocks">
                      {renderSlateContent(hero.contentBlocks)}
                    </div>
                  )}
                </div>
                {(isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0) && (
                  <div className={`custom-hero-buttons${hero.smallButtons ? ' small-buttons' : ''}`}>
                    {isFirst && hero.useTOC
                      ? tocEntries.map((entry, i) => (
                          <a key={i} href={`#${entry.id}`} className="hero-button white-outline">
                            {entry.title}
                          </a>
                        ))
                      : hero.buttons.map((btn, i) => {
                          const href = getHref(btn.link);
                          const buttonStyle = btn.buttonStyle || 'white-outline';
                          const isExternal = (() => {
                            try {
                              const url = new URL(href, window.location.origin);
                              return url.hostname !== window.location.hostname;
                            } catch {
                              return false;
                            }
                          })();
                          return (
                            <a
                              key={i}
                              href={href}
                              className={`hero-button ${buttonStyle}`}
                              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            >
                              {btn.label || 'Click here'}
                            </a>
                          );
                        })}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CarouselControls = ({ emblaApi, hideNav, hideButtons, controlsColor }) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = React.useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = React.useState(true);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState([]);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onDotButtonClick = useCallback((index) => {
    if (!emblaApi) return;
    emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  if (hideNav && hideButtons) return null;

  return (
    <div className={`embla__controls controls-${controlsColor}`}>
      {!hideButtons && (
        <div className="embla__buttons">
          <button
            className="embla__button"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            className="embla__button"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      )}
      {!hideNav && (
        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`embla__dot${index === selectedIndex ? ' embla__dot--selected' : ''}`}
              onClick={() => onDotButtonClick(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const View = (props) => {
  const { data, properties } = props;

  useEffect(() => {
    if (data?.useTOC) {
      const timeoutId = setTimeout(() => {
        const tocEntries = extractTOCEntries(
          properties?.blocks || {}, 
          properties?.blocks_layout?.items || [], 
          data.useH2 ?? true, 
          data.useH3 ?? true, 
          data.useBlockTitles ?? false
        );
        
        tocEntries.forEach(entry => {
          if (entry.isBlockTitle && entry.blockId) {
            const blockElement = document.querySelector(`[data-block="${entry.blockId}"]`);
            if (blockElement && !blockElement.id) {
              blockElement.id = entry.id;
              if (blockElement.classList.contains('block')) {
                blockElement.setAttribute('data-hero-toc-anchor', 'true');
              }
            }
          }
        });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [data?.useTOC, data?.useBlockTitles, properties?.blocks, properties?.blocks_layout]);

  // Determine display mode and layout mode
  const displayMode = data.displayMode || 'individual';
  const carouselEnabled = data.carouselEnabled || false;
  const carouselDisplayMode = data.carouselDisplayMode || 'individual';
  // Backward compatibility: convert old 'imageStack' to 'imageStackRight'
  let layoutMode = data.layoutMode || 'standard';
  if (layoutMode === 'imageStack') {
    layoutMode = 'imageStackRight';
  }
  
  // Build heroes array
  let heroes = data.heroes || [];
  
  // Always construct the first hero from root-level fields
  const firstHero = {
    heroId: data.heroId,
    preheader: data.preheader,
    title: data.title,
    subtitle: data.subtitle,
    contentBlocks: data.contentBlocks,
    backgroundImage: data.backgroundImage,
    backgroundVideo: data.backgroundVideo,
    backgroundColor: data.backgroundColor,
    foregroundColor: data.foregroundColor || 'light',
    buttons: data.buttons,
    alignment: data.alignment,
    horizontalLayout: data.horizontalLayout,
    topPaddingDesktop: data.topPaddingDesktop,
    topPaddingMobile: data.topPaddingMobile,
    useTOC: data.useTOC,
    useH2: data.useH2,
    useH3: data.useH3,
    useBlockTitles: data.useBlockTitles,
    smallButtons: data.smallButtons,
  };
  
  // Carousel mode: only use additional heroes
  if (carouselEnabled && heroes.length > 0) {
    heroes = heroes;
  } else if (data.multipleHeroes && heroes.length > 0) {
    // Regular multiple heroes: include first hero + additional heroes
    heroes = [firstHero, ...heroes];
  } else {
    // Single hero mode
    heroes = [firstHero];
  }

  const blocks = properties.blocks || {};
  const layout = properties.blocks_layout?.items || [];
  const baseUrl = properties?.['@id'] || '';

  // Calculate shared background if needed
  let sharedBackgroundUrl = '';
  let sharedVideoUrl = null;

  if ((displayMode === 'shared' || (carouselEnabled && carouselDisplayMode === 'shared')) && (carouselEnabled ? true : heroes.length > 0)) {
    const bgSourceHero = carouselEnabled ? firstHero : heroes[0];
    
    if (data.usePreviewImage) {
      const previewImageUrl = `${baseUrl}/@@images/preview_image`;
      const defaultImageUrl = `${baseUrl}/@@images/image`;
      const hasPreviewImage = properties?.preview_image || properties?.image;
      sharedBackgroundUrl = hasPreviewImage && properties?.preview_image ? previewImageUrl : defaultImageUrl;
    } else {
      const image = bgSourceHero.backgroundImage?.[0];
      sharedBackgroundUrl = image ? `${image['@id']}/@@images/image` : '';
    }

    const rawId = bgSourceHero.backgroundVideo?.[0]?.['@id'] || '';
    const filename = rawId.split('/').filter(Boolean).pop();
    const isAlreadyDownloadUrl = rawId.includes('/@@download/');
    sharedVideoUrl = rawId && filename
      ? (isAlreadyDownloadUrl ? rawId : `${rawId}/@@download/file/${filename}`)
      : null;
  }

  const usePageOptions = {
    usePageTitle: data.usePageTitle,
    usePageDescription: data.usePageDescription,
    usePreviewImage: data.usePreviewImage,
    showPublicationDate: data.showPublicationDate,
  };

  // Process decorative images for image stack layout
  const decorativeImages = [
    data.decorativeImage1?.[0] ? `${data.decorativeImage1[0]['@id']}/@@images/image` : null,
    data.decorativeImage2?.[0] ? `${data.decorativeImage2[0]['@id']}/@@images/image` : null,
    data.decorativeImage3?.[0] ? `${data.decorativeImage3[0]['@id']}/@@images/image` : null,
  ];

  const customClass = data.customClass || '';
  const isFullWidth = data.isFullWidth ? 'full-width' : '';
  const isMultiple = (data.multipleHeroes && heroes.length > 1) || carouselEnabled ? 'multiple-heroes' : '';
  const isCarousel = carouselEnabled ? 'carousel-mode' : '';

  // Carousel settings
  const autoplayEnabled = data.carouselAutoplay || false;
  const autoplayDelay = data.carouselAutoplayDelay || 5000;
  const hideNav = data.carouselHideNav || false;
  const hideButtons = data.carouselHideButtons || false;
  const controlsColor = data.carouselControlsColor || 'light';
  
  // Setup Embla if carousel mode
  const autoplayPlugin = autoplayEnabled ? Autoplay({ delay: autoplayDelay, stopOnInteraction: false }) : null;
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true },
    autoplayPlugin ? [autoplayPlugin] : []
  );

  // Determine if we should apply shared background to wrapper
  const activeDisplayMode = carouselEnabled ? carouselDisplayMode : displayMode;
  const applySharedBackground = activeDisplayMode === 'shared';

  // If carousel mode, only render carousel with additional heroes
  if (carouselEnabled) {
    return (
      <>
        {/* Carousel with additional heroes */}
        {heroes.length > 0 && (
          <div 
            className={`custom-hero-block ${isFullWidth} ${isMultiple} ${isCarousel} ${customClass}`.trim()}
            style={applySharedBackground && sharedBackgroundUrl && !sharedVideoUrl ? { 
              backgroundImage: `url(${sharedBackgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            } : {}}
          >
            {applySharedBackground && sharedVideoUrl && (
              <video
                className="hero-shared-video-bg"
                autoPlay
                muted
                loop
                playsInline
                poster={sharedBackgroundUrl}
              >
                <source src={sharedVideoUrl} type="video/mp4" />
              </video>
            )}
            
            <div className="embla" ref={emblaRef}>
              <div className="embla__container">
                {heroes.map((hero, index) => (
                  <HeroItem
                    key={index}
                    hero={hero}
                    properties={properties}
                    isFirst={false}
                    usePageOptions={{}}
                    blocks={blocks}
                    layout={layout}
                    displayMode={carouselDisplayMode}
                    sharedBackgroundUrl={sharedBackgroundUrl}
                    sharedVideoUrl={sharedVideoUrl}
                    heroIndex={index + 1}
                    carouselMode={true}
                    layoutMode={layoutMode}
                    decorativeImages={decorativeImages}
                    
                  />
                ))}
              </div>
            </div>
            
            <CarouselControls 
              emblaApi={emblaApi} 
              hideNav={hideNav} 
              hideButtons={hideButtons}
              controlsColor={controlsColor}
            />
          </div>
        )}
      </>
    );
  }

  // Regular non-carousel mode
  return (
    <div 
      className={`custom-hero-block ${isFullWidth} ${isMultiple} ${customClass}`.trim()}
      style={applySharedBackground && sharedBackgroundUrl && !sharedVideoUrl ? { 
        backgroundImage: `url(${sharedBackgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}}
    >
      {applySharedBackground && sharedVideoUrl && (
        <video
          className="hero-shared-video-bg"
          autoPlay
          muted
          loop
          playsInline
          poster={sharedBackgroundUrl}
        >
          <source src={sharedVideoUrl} type="video/mp4" />
        </video>
      )}
      {heroes.map((hero, index) => (
        <HeroItem
          key={index}
          hero={hero}
          properties={properties}
          isFirst={index === 0}
          usePageOptions={usePageOptions}
          blocks={blocks}
          layout={layout}
          displayMode={displayMode}
          sharedBackgroundUrl={sharedBackgroundUrl}
          sharedVideoUrl={sharedVideoUrl}
          heroIndex={index}
          carouselMode={false}
          layoutMode={layoutMode}
          decorativeImages={decorativeImages}
          
        />
      ))}
    </div>
  );
};

export default View;
